import * as _ from 'lodash';
import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';
import type {
  PackageLabelAssignmentWithName,
  PackageLabelScope,
} from '../../../../shared/model-typings/packagelabelassignment';
import { BUTTON_TYPE, FormGroup, Icon, LabeledMultiTextInput, LabeledSelect, Select, icons } from '../../generic';
import InfoPanel from '../../generic/InfoPanel';
import Loader from '../../generic/Loader';
import { Tab } from './types';
import type { LabelOption, VersionedLabelOption } from './types';

import Locator from '../../../../shared/Locator';
import Button, { BUTTON_SIZE } from '../../generic/Button';
import styles from './ModalManagePackageLabels.module.less';
import type { SelectOption } from '../../../types';
import type { usePackageLabelState } from './usePackageLabelState';

const TabScopes: Record<Tab, PackageLabelScope> = {
  [Tab.Revision]: 'revision',
  [Tab.Project]: 'project',
  [Tab.Global]: 'org',
};

const AllLabelsSelected: VoidFunctionComponent<{ inputValue: string }> = () => (
  <span>All available labels have been selected. Create new labels in your organization settings.</span>
);

type Props = {
  tab: Tab;
  activeTab: Tab;
  helpText: string;
  parentRevisionLocator?: string;
  state: ReturnType<typeof usePackageLabelState>;
  labelsSelected: VersionedLabelOption;
  setLabelsSelected: (labels: VersionedLabelOption) => void;
  hasNoPermission: boolean;
};

const TabContent: VoidFunctionComponent<Props> = ({
  tab,
  activeTab,
  helpText,
  parentRevisionLocator,
  state,
  labelsSelected,
  setLabelsSelected,
  hasNoPermission,
}) => {
  useEffect(() => {
    state.initializeGlobalSelected();
    state.initializeProjectSelected(Locator.parsePackageString(parentRevisionLocator!));
    state.initializeRevisionSelected(parentRevisionLocator!);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to refresh this effect when the label assignments change
  }, [state.labelAssignments]);

  if (tab !== activeTab) {
    return null;
  }

  if (state.isLoadingLabels || state.isLoadingLabelAssignments) {
    return <Loader />;
  }

  const labelMap = _.keyBy(state.labels?.packageLabels, (label) => label.id);
  const labelOptions = state.labels?.packageLabels?.map((label) => ({ value: label.id, label: label.name }));

  const isScopeMatch = (assignment: PackageLabelAssignmentWithName) => {
    return assignment.scope === TabScopes[tab];
  };

  const isParentProjectMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'project') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === Locator.parsePackageString(parentRevisionLocator!);
  };

  const isParentRevisionMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'revision') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === parentRevisionLocator;
  };

  const assignments = state.labelAssignments?.packageLabelAssignments?.filter(
    (assignment) => isScopeMatch(assignment) && isParentProjectMatch(assignment) && isParentRevisionMatch(assignment),
  );
  const [allVersionAssignments, specificVersionAssignments] = _.partition(
    assignments,
    (assignment) => assignment.packageVersion == null,
  );
  const defaultAllVersionAssignments = allVersionAssignments.map((assignment) => ({
    value: assignment.labelId,
    label: labelMap[assignment.labelId]?.name,
  }));

  const defaultSpecificVersionAssignments = _.mapValues(
    _.groupBy(specificVersionAssignments, (assignment) => assignment.packageVersion),
    (assignmentGroup) =>
      assignmentGroup.map((assignment) => ({ value: assignment.labelId, label: labelMap[assignment.labelId]?.name })),
  );

  const onChange = (selectedLabels: Array<SelectOption<number>>, rawVersion: string | null = null) => {
    const version = rawVersion ?? 'all';
    let versionMap: VersionedLabelOption = {};

    if (_.isEmpty(selectedLabels)) {
      // remove all labels for this version if the user clears the selection
      versionMap = { [version]: new Set() };
    } else {
      // reconcile the new and existing selections
      versionMap = selectedLabels.reduce((acc, label) => {
        return {
          ...acc,
          [version as string]: (acc[version as string] || new Set()).add(label.value),
        };
      }, {} as VersionedLabelOption);
    }
    setLabelsSelected({ ...labelsSelected, ...versionMap });
  };

  if (hasNoPermission) {
    return <InfoPanel>You do not have permission to make changes here.</InfoPanel>;
  }

  return (
    <>
      <p className={styles.helpText}>{helpText}</p>
      <FormGroup>
        <LabeledSelect
          id="all-versions"
          label="All versions"
          isMulti
          onChange={(selectedVersions: LabelOption[]) => onChange(selectedVersions, null)}
          options={labelOptions}
          closeMenuOnSelect={false}
          noOptionsMessage={AllLabelsSelected}
          placeholder="Assign labels..."
          defaultValue={defaultAllVersionAssignments}
        />
        {Object.entries(defaultSpecificVersionAssignments).map(([version, versionAssignments]) => (
          <div key={version}>
            <LabeledMultiTextInput
              id="specific-versions"
              label="Specific Versions"
              values={[version]}
              maxItems={10}
              shouldAllowDuplicates={false}
              isCaseInsensitive
              placeholder="Package versions to assign labels to"
            />
            <Select
              id="specific-version-labels"
              isMulti
              onChange={(selectedLabels: LabelOption[]) => onChange(selectedLabels, version)}
              options={labelOptions}
              defaultValue={versionAssignments}
              closeMenuOnSelect={false}
              noOptionsMessage={AllLabelsSelected}
              placeholder="Assign labels..."
            />
            <div className={styles.removeButton}>
              <Button title="Remove" onClick={() => null} type={BUTTON_TYPE.TEXT} size={BUTTON_SIZE.MEDIUM}>
                <span className={styles.removeText}>
                  <Icon icon={icons.xCircle} />
                  Remove
                </span>
              </Button>
            </div>
          </div>
        ))}
        <Button
          icon={icons.plusCircleO}
          title="Remove"
          onClick={() => null}
          type={BUTTON_TYPE.TEXT}
          size={BUTTON_SIZE.MEDIUM}
        >
          Add Version
        </Button>
      </FormGroup>
    </>
  );
};

export default TabContent;
